import { useState, useEffect } from 'react'
import { BannerModal } from '../../../Components/Modal/BannerModal'
import { Box } from '@mui/material'
import { sendGet, sendPut } from '../../../hooks/use-fetch'
import { SelectWithLabel } from '../../../Components/SelectWithLabel'
import { DatePickerWithLabel } from '../../../Components/DatePicker'
import { type lookup, type postError } from '../../../core/types'
import { toast } from 'react-toastify'

interface EnrollmentEdit {
  id: number
  sessionId: number | null
  isdServiceProviderProgramId: number
  status: string
  exitReasonId: number
  notes: string | null
  startDate: Date | null
  deliveryScheduleId: number | null
  waitlistStartDate: Date | null
  exitDate: Date | null
  facilityId: number
  sessionBlockId: number
  isdid: number
  serviceProviderId: number
  programId: number
  isStateProgram: boolean
}

interface EnrollmentEditModalProps {
  enrollmentID: number
  onClose: (updated: boolean) => void
}

export function EnrollmentEditModal (props: EnrollmentEditModalProps): JSX.Element {
  const [enrollment, setEnrollment] = useState<EnrollmentEdit | null>(null)
  const [scheduleList, setScheduleList] = useState<lookup[]>([])
  const [exitReasonList, setExitReasonList] = useState<lookup[]>([])
  const [facilityList, setFacilityList] = useState<lookup[]>([])
  const [sessionList, setSessionList] = useState<lookup[]>([])
  const [hasStarted, setHasStarted] = useState(false)
  const [postErrors, setPostErrors] = useState<postError[]>([])

  useEffect(() => {
    const loadData = async (): Promise<void> => {
      const { response } = await sendGet(`/Enrollment/GetEditable?id=${props.enrollmentID}`, {})
      if (response != null) {
        setEnrollment(response)
        setHasStarted(response.status === 'Enrolled' || response.status === 'Exited')
      }
      const { response: schedList } = await sendGet('/Enrollment/GetDeliverySchedules', {})
      setScheduleList(schedList)
      const { response: reasonList } = await sendGet('/Enrollment/GetExitReasons', {})
      setExitReasonList(reasonList)

      const enroll = response as EnrollmentEdit
      const { response: facList } = await sendGet(`/ServiceProviderDetails/GetProviderFacilities?isdid=${enroll.isdid}&spId=${enroll.serviceProviderId}&pId=${enroll.programId}`, {})
      if (facList !== undefined && facList.length > 0) {
        setFacilityList(facList)
      } else {
        setFacilityList([])
      }
    }
    void loadData()
  }, [props.enrollmentID])

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      if (enrollment == null) return

      const { response: sessionList } = await sendGet(`/SessionList/GetSessions?blockId=${enrollment.sessionBlockId}&facilityId=${enrollment.facilityId ?? 0}`, {})
      setSessionList(sessionList)
    }
    void fetchData()
  }, [props.enrollmentID, enrollment?.sessionBlockId ?? 0, enrollment?.facilityId ?? 0])

  const valueChange = (field: string, e: any): void => {
    if (enrollment !== null) {
      setEnrollment({ ...enrollment, [field]: e })
    }
  }

  const hasError = (field: string): boolean => {
    return postErrors.find((e: postError) => e.field.toLowerCase() === field.toLowerCase()) !== undefined
  }

  const handleSubmit = async (): Promise<void> => {
    if (enrollment !== null) {
      const { response } = await sendPut('/Enrollment/UpdateEnrollment', enrollment)
      if (response.errors.length === 0) {
        props.onClose(true)
        toast.success('Enrollment updated successfully')
      } else {
        setPostErrors(response.errors)
        response.errors.forEach((e: postError) => toast.error(e.error))
      }
    }
  }

  if (enrollment == null) return (<></>)

  const content = <Box sx={{ p: '0 20px' }}>
    <SelectWithLabel
      width='75%'
      label='Delivery Schedule'
      name='deliveryScheduleId'
      required={hasStarted && enrollment.isStateProgram}
      includeNoneOption={true}
      onChange={(evt: any) => { valueChange('deliveryScheduleId', evt.target.value) }}
      options={scheduleList}
      value={enrollment.deliveryScheduleId ?? ''}
      error={hasError('deliveryScheduleId')}
    />
    <SelectWithLabel
      width='75%'
      label='Facility'
      name='facilityId'
      required={true}
      onChange={(evt: any) => { valueChange('facilityId', evt.target.value) }}
      options={facilityList}
      value={enrollment.facilityId}
      error={hasError('facilityId')}
    />
    <SelectWithLabel
      width='75%'
      label='Session'
      name='sessionId'
      includeNoneOption={true}
      onChange={(evt: any) => { valueChange('sessionId', evt.target.value) }}
      options={sessionList}
      value={enrollment.sessionId ?? ''}
      error={hasError('sessionId')}
    />
    {enrollment.status !== 'Pending' && <DatePickerWithLabel
      label='Wait List Start Date'
      name='waitlistStartDate'
      dataTestId='waitlistStartDate'
      required={true}
      onChange={(date: Date | null) => { valueChange('waitlistStartDate', date ?? null) }}
      value={enrollment.waitlistStartDate}
      error={hasError('waitlistStartDate')}
    />
    }
    {hasStarted && <DatePickerWithLabel
      label='Start Date'
      name='startDate'
      dataTestId='startDate'
      required={true}
      onChange={(date: Date | null) => { valueChange('startDate', date ?? null) }}
      value={enrollment.startDate}
      error={hasError('startDate')}
    />
    }
    {enrollment.status === 'Exited' && <>
      <DatePickerWithLabel
        label='Exit Date'
        name='exitDate'
        dataTestId='exitDate'
        required={true}
        onChange={(date: Date | null) => { valueChange('exitDate', date ?? null) }}
        value={enrollment.exitDate}
        error={hasError('exitDate')}
      />
      <SelectWithLabel
        width='75%'
        label='Exit Reason'
        name='exitReasonId'
        required={true}
        includeNoneOption={true}
        onChange={(evt: any) => { valueChange('exitReasonId', evt.target.value) }}
        options={exitReasonList}
        value={enrollment.exitReasonId ?? ''}
        error={hasError('exitReasonId')}
      />
    </>
    }
  </Box>

  return <BannerModal
    className='Nested Referral'
    maxWidth='sm'
    open={true}
    content={content}
    onClose={() => { props.onClose(false) }}
    onConfirm={handleSubmit}
    title='Edit Enrollment'
    confirmButtonText='Update'
  />
}
